import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { Loader2, UploadCloud, Copy, Check, Trash2, Film } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (p) => (p && p.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p}` : p);

export const MediaLibrary = ({ onPick, compact = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/media`); setItems(data); }
    catch (e) { /* shell handles auth */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/media/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      if (onPick) onPick(data.path);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Upload failed. Images and videos only.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copy = async (p) => {
    try { await navigator.clipboard.writeText(p); setCopied(p); setTimeout(() => setCopied(""), 1800); } catch (e) { /* noop */ }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this media file?")) return;
    try { await axios.delete(`${API}/admin/media/${id}`); await load(); } catch (e) { /* noop */ }
  };

  return (
    <div data-testid="media-library">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          {!compact && <p className="text-neutral-400 text-sm">High-quality images and video for your features. Upload once, reuse anywhere.</p>}
        </div>
        <label data-testid="media-upload-button" className="cursor-pointer inline-flex items-center gap-2 px-5 h-11 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#E30613] text-white hover:bg-[#c00510] transition-colors">
          {uploading ? <Loader2 className="animate-spin" size={14} /> : <UploadCloud size={14} />}
          {uploading ? "Uploading…" : "Upload Media"}
          <input ref={fileRef} type="file" accept="image/*,video/*" onChange={upload} className="hidden" data-testid="media-file-input" />
        </label>
      </div>

      {error && <p data-testid="media-error" className="text-[#E30613] text-sm mb-4">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E30613]" size={24} /></div>
      ) : items.length === 0 ? (
        <p className="text-neutral-600 text-sm py-10" data-testid="media-empty">No media uploaded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="media-grid">
          {items.map((m) => (
            <div key={m.id} className="group relative border border-white/10 overflow-hidden" data-testid={`media-item-${m.id}`}>
              <div className="aspect-[4/3] bg-black/60 flex items-center justify-center overflow-hidden">
                {m.kind === "video" ? (
                  <video src={abs(m.path)} className="w-full h-full object-cover" muted playsInline />
                ) : (
                  <img src={abs(m.path)} alt={m.name} className="w-full h-full object-cover grayscale-img" loading="lazy" />
                )}
                {m.kind === "video" && <Film size={22} className="absolute text-white/80 drop-shadow" />}
              </div>
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                {onPick && (
                  <button onClick={() => onPick(m.path)} data-testid={`media-pick-${m.id}`} className="w-full px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold bg-[#E30613] text-white">Use This</button>
                )}
                <button onClick={() => copy(m.path)} data-testid={`media-copy-${m.id}`} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border border-white/40 text-white hover:bg-white/10">
                  {copied === m.path ? <Check size={12} /> : <Copy size={12} />} {copied === m.path ? "Copied" : "Copy URL"}
                </button>
                <button onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-neutral-300 hover:text-[#E30613]">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
